import React from 'react';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

// Matches server checklist (no separate "queued" — DB may still be queued pre-worker).
const HAPPY_PATH = [
  'processing_video',
  'quality_check',
  'point_extraction',
  'point_analysis',
  'finalizing',
  'complete',
];

const STAGE_TIMING_KEY = {
  processing_video: 'video_processing',
  quality_check: 'quality_check',
  point_extraction: 'point_extraction',
};

/** Whole seconds for display (e.g. 12.94 → "12s"). */
function formatDurationSeconds(value) {
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return null;
  return `${Math.round(n)}s`;
}

function durationForServerStage(stageId, pipelineTiming) {
  if (!pipelineTiming) return null;
  if (stageId === 'point_analysis') {
    const v =
      pipelineTiming.point_analysis_max_seconds ??
      pipelineTiming.point_analysis_sum_seconds;
    if (v != null) return formatDurationSeconds(v);
  }
  const key = STAGE_TIMING_KEY[stageId];
  if (key != null && pipelineTiming[key] != null) {
    return formatDurationSeconds(pipelineTiming[key]);
  }
  if (stageId === 'finalizing') {
    const a = Number(pipelineTiming.finalize_analysis);
    const b = Number(pipelineTiming.extract_analysis_frames);
    const hasA = !Number.isNaN(a) && a > 0;
    const hasB = !Number.isNaN(b) && b > 0;
    if (hasA && hasB) return formatDurationSeconds(a + b);
    if (hasA) return formatDurationSeconds(a);
    if (hasB) return formatDurationSeconds(b);
  }
  return null;
}

function labelForStage(stageId, pipelineStages) {
  const row = pipelineStages.find((s) => s.id === stageId);
  if (row?.label) return row.label;
  return String(stageId || '').replaceAll('_', ' ') || '—';
}

/**
 * Checklist: client handoff row (S3 upload or example prep) + server pipeline stages (append-only).
 * uploadRow.label overrides the default "Uploading video" (e.g. example clip prep).
 */
export function SessionPipelineChecklist({
  uploadRow,
  statusData,
  debugMode = false,
  pipelineTiming = null,
}) {
  const pipelineStages = statusData?.pipeline?.stages || [];
  const stage = statusData?.stage || 'processing_video';
  const pointProcessing = statusData?.point_processing;
  const totalPoints = pointProcessing?.total ?? 0;
  const processedPoints = pointProcessing?.processed ?? 0;

  const curIdx =
    stage === 'queued' ? 0 : HAPPY_PATH.indexOf(stage);
  const safeIdx = curIdx >= 0 ? curIdx : 0;

  const rows = [];

  if (uploadRow) {
    let uploadMeta = uploadRow.meta;
    if (
      debugMode &&
      pipelineTiming?.video_upload != null &&
      uploadRow.state === 'complete'
    ) {
      const u = formatDurationSeconds(pipelineTiming.video_upload);
      if (u) {
        uploadMeta = uploadMeta ? `${uploadMeta} · ${u}` : u;
      }
    }
    rows.push({
      key: 'uploading',
      label: uploadRow.label || 'Uploading video',
      labelSuffix: null,
      meta: uploadMeta,
      state: uploadRow.state,
    });
  }

  const showServerStages =
    !uploadRow ||
    uploadRow.state === 'complete' ||
    uploadRow.state === 'error';

  if (showServerStages && uploadRow?.state !== 'error') {
    for (let i = 0; i <= safeIdx; i += 1) {
      const id = HAPPY_PATH[i];
      const isLast = i === safeIdx;
      const state = isLast ? 'in_progress' : 'complete';
      const labelSuffix =
        id === 'point_analysis' && totalPoints > 0
          ? `${Math.min(processedPoints, totalPoints)}/${totalPoints}`
          : null;
      let meta = null;
      if (debugMode) {
        const dur = durationForServerStage(id, pipelineTiming);
        // Finalizing writes step seconds as work proceeds; hide until the row is done.
        if (dur && !(id === 'finalizing' && state === 'in_progress')) {
          meta = dur;
        }
      }
      rows.push({
        key: `server-${id}`,
        label: labelForStage(id, pipelineStages),
        labelSuffix,
        meta,
        state,
      });
    }
  }

  return (
    <ul className="w-full max-w-sm text-left space-y-3 mt-6">
      {rows.map((row) => (
        <li
          key={row.key}
          className="flex items-start gap-3 rounded-xl border border-white/10 bg-beachvision-surface-low/80 px-3 py-2.5"
        >
          <span className="mt-0.5 shrink-0" aria-hidden>
            {row.state === 'complete' ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            ) : row.state === 'error' ? (
              <AlertCircle className="h-5 w-5 text-red-400" />
            ) : (
              <Loader2 className="h-5 w-5 text-beachvision-coral animate-spin" />
            )}
          </span>
          <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
            <span className="text-white text-sm font-medium capitalize min-w-0">
              {row.label}
              {row.labelSuffix ? (
                <span className="text-beachvision-muted text-xs tabular-nums font-normal normal-case whitespace-nowrap ml-1.5">
                  {row.labelSuffix}
                </span>
              ) : null}
            </span>
            {row.meta ? (
              <span className="text-beachvision-muted text-xs tabular-nums shrink-0">{row.meta}</span>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}
